import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 * @property {HTMLElement} [stickyVariantPicker] - Optional variant picker wrapper
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Sync sticky variant picker (swatches/dropdowns) -> main variant picker
    this.refs.stickyVariantPicker?.addEventListener('click', this.#handleStickySwatchClick, { signal });
    this.refs.stickyVariantPicker?.addEventListener('change', this.#handleStickyDropdownChange, { signal });
    this.refs.sellingPlanSelect?.addEventListener(
  'change',
  this.#handleStickySellingPlanChange,
  { signal }
);

    this.#getInitialQuantity();
    this.#updateStickySellingPlanPrice();


    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }


#updateStickySellingPlanPrice = () => {
  const select = this.refs.sellingPlanSelect;

  if (!select) {
    console.log('[Sticky ATC] Selling plan select not found');
    return;
  }

  const selectedOption = select.selectedOptions[0];

  if (!selectedOption) {
    console.log('[Sticky ATC] No selling plan selected');
    return;
  }

  const price = selectedOption.dataset.sellingPlanPrice;

  if (!price) {
    console.log(
      '[Sticky ATC] No price found for selected selling plan'
    );
    return;
  }

  const stickyPrice = this.querySelector(
    '[ref="stickyAddToCartPrice"]'
  );

  if (!stickyPrice) {
    console.log(
      '[Sticky ATC] Sticky price element not found'
    );
    return;
  }

  console.log(
    '[Sticky ATC] Updating price:',
    price
  );

  stickyPrice.textContent = price;
};


#handleStickySellingPlanChange = () => {
  const select = this.refs.sellingPlanSelect;

  if (!select) return;

  const sellingPlan = select.value;

  console.log(
    '[Sticky ATC] Selling plan changed:',
    sellingPlan || 'Buy Once'
  );

  /*
   * Update sticky price immediately
   */
  this.#updateStickySellingPlanPrice();

  /*
   * Sync selling plan to the main product form
   */
  const productForm = this.#getProductForm();

  if (!productForm) {
    console.log(
      '[Sticky ATC] Product form not found'
    );
    return;
  }

  let sellingPlanInput = productForm.querySelector(
    'input[name="selling_plan"]'
  );

  if (!sellingPlanInput) {
    sellingPlanInput = document.createElement('input');
    sellingPlanInput.type = 'hidden';
    sellingPlanInput.name = 'selling_plan';

    productForm.appendChild(sellingPlanInput);
  }

  sellingPlanInput.value = sellingPlan;

  sellingPlanInput.dispatchEvent(
    new Event('change', {
      bubbles: true
    })
  );
};


  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // Sync selling plan selection to the main product form before triggering its submit
    this.#syncSellingPlanToMainForm();

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
 * Syncs the sticky bar's selling plan selection into the main product form,
 * since the sticky bar's Add to Cart button just triggers the main form's button.
 */
#syncSellingPlanToMainForm() {
  const sellingPlanSelect = this.refs.sellingPlanSelect;
  if (!sellingPlanSelect) return;

  const productForm = this.#getProductForm();
  if (!productForm) return;

  const mainSellingPlanInput = productForm.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
  if (!mainSellingPlanInput) return;

  mainSellingPlanInput.value = sellingPlanSelect.value;
  mainSellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Forwards a sticky swatch click to the matching radio input in the main variant picker.
 * @param {MouseEvent} event
 */
#handleStickySwatchClick = (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const button = target.closest('.sticky-add-to-cart__swatch');
  if (!button) return;

  const valueId = /** @type {HTMLElement} */ (button).dataset.optionValueId;
  const valueName = /** @type {HTMLElement} */ (button).dataset.valueName;
  const optionName = /** @type {HTMLElement} */ (button).dataset.optionName;
  if (!valueId) return;

  // Update the label immediately for instant feedback
  const swatchGroup = button.closest('.sticky-add-to-cart__swatch-group');
  const label = swatchGroup?.querySelector('[data-option-selected-label]');

  if (label && valueName && optionName) {
  if (optionName === 'Color') {
    label.textContent = valueName;
  } else {
    label.textContent = `${optionName}: ${valueName}`;
  }
}

  // Update visual selected state on the clicked swatch
  swatchGroup?.querySelectorAll('.sticky-add-to-cart__swatch').forEach((el) => el.classList.remove('is-selected'));
  button.classList.add('is-selected');

  const mainPicker = document.querySelector('variant-picker[ref="mainVariantPicker"]')
    ?? document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
  if (!mainPicker) return;

  const mainInput = mainPicker.querySelector(`input[data-option-value-id="${valueId}"]`);
  if (mainInput instanceof HTMLInputElement && !mainInput.checked) {
    mainInput.checked = true;
    mainInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

/**
 * Forwards a sticky dropdown change to the matching select in the main variant picker.
 * @param {Event} event
 */
// #handleStickyDropdownChange = (event) => {
  
//   const select = /** @type {HTMLSelectElement} */ (event.target);
//   if (!select.matches('.sticky-add-to-cart__option-select')) return;

//   const selectedOption = select.selectedOptions[0];
//   const valueId = selectedOption?.dataset.optionValueId;
//   const optionName = select.dataset.optionName;
//   if (!valueId) return;

//   // Update the label immediately for instant feedback
//   const dropdownGroup = select.closest('.sticky-add-to-cart__dropdown-group');
//   const label = dropdownGroup?.querySelector('.sticky-add-to-cart__dropdown-label');


//   // if (label && optionName) {
//   //   label.textContent = `${optionName}: ${selectedOption.textContent.trim()}`;
//   // }

//   if (label && optionName) {
//   label.textContent = optionName;
//  }

//   const mainPicker = document.querySelector('variant-picker[ref="mainVariantPicker"]')
//     ?? document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
//   if (!mainPicker) return;

//   const mainOption = mainPicker.querySelector(`option[data-option-value-id="${valueId}"]`);
//   if (mainOption instanceof HTMLOptionElement) {
//     const mainSelect = mainOption.closest('select');
//     if (mainSelect) {
//       mainSelect.value = mainOption.value;
//       mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
//     }
//   }
// };

/**
 * Forwards a sticky dropdown change to the matching input
 * in the main variant picker.
 * @param {Event} event
 */
#handleStickyDropdownChange = (event) => {
  const select = /** @type {HTMLSelectElement} */ (event.target);

  if (!select.matches('.sticky-add-to-cart__option-select')) return;


  const selectedOption = select.selectedOptions[0];
  const valueId = selectedOption?.dataset.optionValueId;
  const optionName = select.dataset.optionName;

  if (!valueId) return;

  // Update the label
  const dropdownGroup = select.closest(
    '.sticky-add-to-cart__dropdown-group'
  );

  const label = dropdownGroup?.querySelector(
    '.sticky-add-to-cart__dropdown-label'
  );

  if (label && optionName) {
    label.textContent = optionName;
  }

  const mainPicker =
    document.querySelector('variant-picker[ref="mainVariantPicker"]') ??
    document.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );

  if (!mainPicker) {
    return;
  }


  /*
   * The main variant picker uses radio inputs,
   * not <option> elements.
   */
  const mainInput = mainPicker.querySelector(
    `input[data-option-value-id="${valueId}"]`
  );

  console.log('STICKY mainInput:', mainInput);

  if (mainInput instanceof HTMLInputElement) {
    if (!mainInput.checked) {
      mainInput.checked = true;

      mainInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }
  }
};

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */



  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });
        this.#updateStickySellingPlanPrice();

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
